<template>
  <div class="chat-page">
    <!-- Header -->
    <div class="chat-header">
      <div class="header-content">
        <div class="chat-title">
          <i class="fas fa-robot"></i>
          <h1>Assistant IA EmailAuto</h1>
          <span class="model-indicator" :class="{ 'online': isConnected }">
            {{ currentModel }}
          </span>
        </div>
        <div class="header-actions">
          <div class="model-selector">
            <select v-model="selectedModel" @change="switchModel">
              <optgroup label="Modèles locaux (Ollama)">
                <option value="llama2">Llama 2</option>
                <option value="codellama">Code Llama</option>
                <option value="mistral">Mistral</option>
              </optgroup>
              <optgroup label="Modèles OpenAI">
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </optgroup>
            </select>
          </div>
          <button 
            class="btn btn-outline"
            @click="clearConversation"
            :disabled="messages.length === 0"
          >
            <i class="fas fa-trash"></i>
            Effacer
          </button>
          <button 
            class="btn btn-outline"
            @click="exportConversation"
            :disabled="messages.length === 0"
          >
            <i class="fas fa-download"></i>
            Exporter
          </button>
        </div>
      </div>
    </div>

    <!-- Chat Container -->
    <div class="chat-container">
      <!-- Sidebar avec contexte -->
      <div class="chat-sidebar" :class="{ 'collapsed': sidebarCollapsed }">
        <div class="sidebar-header">
          <h3>Contexte</h3>
          <button 
            class="sidebar-toggle"
            @click="sidebarCollapsed = !sidebarCollapsed"
            :aria-expanded="!sidebarCollapsed"
            aria-label="Afficher / masquer le contexte"
          >
            <i class="fas" :class="sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
          </button>
        </div>
        
        <div class="sidebar-content" v-if="!sidebarCollapsed">
          <!-- Conversations récentes -->
          <div class="context-section">
            <h4>Conversations récentes</h4>
            <div class="conversation-list">
              <div 
                v-for="conv in recentConversations" 
                :key="conv.id"
                class="conversation-item"
                :class="{ 'active': conv.id === currentConversationId }"
                @click="loadConversation(conv.id)"
              >
                <div class="conversation-title">{{ conv.title }}</div>
                <div class="conversation-date">{{ formatDate(conv.date) }}</div>
              </div>
            </div>
          </div>
          
          <!-- Contexte métier -->
          <div class="context-section">
            <h4>Contexte métier</h4>
            <div class="context-toggles">
              <label class="context-toggle">
                <input 
                  type="checkbox" 
                  v-model="contextSettings.includeEmails"
                  @change="updateContext"
                >
                <span>Emails récents</span>
              </label>
              <label class="context-toggle">
                <input 
                  type="checkbox" 
                  v-model="contextSettings.includeCommandes"
                  @change="updateContext"
                >
                <span>Commandes en cours</span>
              </label>
              <label class="context-toggle">
                <input 
                  type="checkbox" 
                  v-model="contextSettings.includeClients"
                  @change="updateContext"
                >
                <span>Données clients</span>
              </label>
              <label class="context-toggle">
                <input 
                  type="checkbox" 
                  v-model="contextSettings.includeProduits"
                  @change="updateContext"
                >
                <span>Catalogue produits</span>
              </label>
            </div>
          </div>
          
          <!-- Prompts prédéfinis -->
          <div class="context-section">
            <h4>Prompts rapides</h4>
            <div class="quick-prompts">
              <button 
                v-for="prompt in quickPrompts" 
                :key="prompt.id"
                class="quick-prompt-btn"
                @click="useQuickPrompt(prompt)"
              >
                <i :class="prompt.icon"></i>
                {{ prompt.title }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Zone de chat principale -->
      <div class="chat-main">
        <!-- Messages -->
        <div class="messages-container" ref="messagesContainer">
          <div class="welcome-message" v-if="messages.length === 0">
            <div class="welcome-icon">
              <i class="fas fa-robot"></i>
            </div>
            <h2>Bonjour ! Je suis votre assistant IA EmailAuto</h2>
            <p>Je peux vous aider avec :</p>
            <ul>
              <li>Analyse des emails et extraction de commandes</li>
              <li>Détection d'anomalies dans les données</li>
              <li>Recherche dans votre base de données</li>
              <li>Génération de rapports et statistiques</li>
              <li>Assistance technique et configuration</li>
            </ul>
            <p>Posez-moi une question pour commencer !</p>
          </div>
          
          <div 
            v-for="(message, index) in messages" 
            :key="index"
            class="message"
            :class="{ 'user': message.role === 'user', 'assistant': message.role === 'assistant' }"
          >
            <div class="message-avatar">
              <i v-if="message.role === 'user'" class="fas fa-user"></i>
              <i v-else class="fas fa-robot"></i>
            </div>
            <div class="message-content">
              <div class="message-header">
                <span class="message-role">{{ message.role === 'user' ? 'Vous' : 'Assistant' }}</span>
                <span class="message-time">{{ formatTime(message.timestamp) }}</span>
              </div>
              <div class="message-text" v-html="formatMessage(message.content)"></div>
              
              <!-- Actions sur les messages -->
              <div class="message-actions" v-if="message.role === 'assistant'">
                <button 
                  class="action-btn"
                  @click="copyMessage(message.content)"
                  title="Copier"
                >
                  <i class="fas fa-copy"></i>
                </button>
                <button 
                  class="action-btn"
                  @click="regenerateResponse(index)"
                  title="Régénérer"
                  :disabled="isLoading"
                >
                  <i class="fas fa-redo"></i>
                </button>
                <button 
                  class="action-btn"
                  @click="rateMessage(message, 'good')"
                  title="Bonne réponse"
                  :class="{ 'active': message.rating === 'good' }"
                >
                  <i class="fas fa-thumbs-up"></i>
                </button>
                <button 
                  class="action-btn"
                  @click="rateMessage(message, 'bad')"
                  title="Mauvaise réponse"
                  :class="{ 'active': message.rating === 'bad' }"
                >
                  <i class="fas fa-thumbs-down"></i>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Indicateur de frappe -->
          <div v-if="isLoading" class="message assistant typing">
            <div class="message-avatar">
              <i class="fas fa-robot"></i>
            </div>
            <div class="message-content">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Zone de saisie -->
        <div class="input-container">
          <div class="input-wrapper">
            <div class="input-actions">
              <button 
                class="action-btn"
                @click="attachFile"
                title="Joindre un fichier"
              >
                <i class="fas fa-paperclip"></i>
              </button>
              <button 
                class="action-btn"
                @click="toggleVoiceInput"
                title="Saisie vocale"
                :class="{ 'active': isListening }"
              >
                <i class="fas" :class="isListening ? 'fa-microphone-slash' : 'fa-microphone'"></i>
              </button>
            </div>
            
            <textarea 
              ref="messageInput"
              v-model="currentMessage"
              placeholder="Tapez votre message... (Shift+Entrée pour nouvelle ligne)"
              @keydown="handleKeydown"
              @input="adjustTextareaHeight"
              :disabled="isLoading"
              rows="1"
            ></textarea>
            
            <button 
              class="send-btn"
              @click="sendMessage"
              :disabled="!currentMessage.trim() || isLoading"
            >
              <i v-if="isLoading" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
            </button>
          </div>
          
          <!-- Fichiers attachés -->
          <div v-if="attachedFiles.length > 0" class="attached-files">
            <div 
              v-for="(file, index) in attachedFiles" 
              :key="index"
              class="attached-file"
            >
              <i class="fas fa-file"></i>
              <span>{{ file.name }}</span>
              <button @click="removeFile(index)" aria-label="Retirer le fichier">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <!-- Suggestions -->
          <div v-if="suggestions.length > 0" class="suggestions">
            <div class="suggestions-header">Suggestions :</div>
            <div class="suggestions-list">
              <button 
                v-for="suggestion in suggestions" 
                :key="suggestion"
                class="suggestion-btn"
                @click="useSuggestion(suggestion)"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de configuration -->
    <div v-if="showConfigModal" class="modal-overlay" @click="closeConfigModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Configuration IA</h3>
          <button class="modal-close" @click="closeConfigModal" aria-label="Fermer">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="config-section">
            <h4>Paramètres du modèle</h4>
            <div class="form-group">
              <label>Température (créativité)</label>
              <input 
                type="range" 
                v-model.number="modelConfig.temperature" 
                min="0" 
                max="2" 
                step="0.1"
              >
              <span>{{ modelConfig.temperature }}</span>
            </div>
            <div class="form-group">
              <label>Tokens maximum</label>
              <input 
                type="number" 
                v-model.number="modelConfig.maxTokens" 
                min="100" 
                max="4000"
              >
            </div>
            <div class="form-group">
              <label>Prompt système</label>
              <textarea 
                v-model="modelConfig.systemPrompt" 
                rows="4"
                placeholder="Instructions pour l'assistant IA..."
              ></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeConfigModal">
            Annuler
          </button>
          <button class="btn btn-primary" @click="saveConfig">
            Sauvegarder
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useChatStore } from '@/stores/chat'
import { marked } from 'marked'
import { debounce } from 'lodash'

export default {
  name: 'Chat',
  setup() {
    const chatStore = useChatStore()
    
    // État local
    const currentMessage = ref('')
    const isLoading = ref(false)
    const isListening = ref(false)
    // Replié par défaut sur mobile / tablette pour laisser la place à la conversation
    const sidebarCollapsed = ref(typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 991.98px)').matches)
    const showConfigModal = ref(false)
    const messagesContainer = ref(null)
    const messageInput = ref(null)
    const attachedFiles = ref([])
    const suggestions = ref([])
    
    // Configuration
    const selectedModel = ref('llama2')
    const currentModel = ref('Llama 2')
    const isConnected = ref(true)
    const currentConversationId = ref(null)
    
    // Messages et conversations
    const messages = ref([])
    const recentConversations = ref([
      {
        id: 1,
        title: 'Analyse des emails du jour',
        date: new Date(Date.now() - 2 * 60 * 60 * 1000)
      },
      {
        id: 2,
        title: 'Détection d\'anomalies commandes',
        date: new Date(Date.now() - 24 * 60 * 60 * 1000)
      },
      {
        id: 3,
        title: 'Rapport mensuel automatisé',
        date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
      }
    ])
    
    // Contexte métier
    const contextSettings = ref({
      includeEmails: true,
      includeCommandes: true,
      includeClients: false,
      includeProduits: false
    })
    
    // Configuration du modèle
    const modelConfig = ref({
      temperature: 0.7,
      maxTokens: 2000,
      systemPrompt: 'Tu es un assistant IA spécialisé dans l\'automatisation du traitement des emails commerciaux. Tu aides les utilisateurs avec l\'analyse des emails, l\'extraction de commandes, la détection d\'anomalies et la gestion des données clients.'
    })
    
    // Prompts rapides
    const quickPrompts = ref([
      {
        id: 1,
        title: 'Analyser les emails non traités',
        icon: 'fas fa-envelope',
        prompt: 'Peux-tu analyser les emails non traités et me donner un résumé des commandes potentielles ?'
      },
      {
        id: 2,
        title: 'Détecter les anomalies',
        icon: 'fas fa-exclamation-triangle',
        prompt: 'Vérifie s\'il y a des anomalies dans les commandes récentes et liste-les par ordre de priorité.'
      },
      {
        id: 3,
        title: 'Rapport de performance',
        icon: 'fas fa-chart-line',
        prompt: 'Génère un rapport de performance pour cette semaine avec les KPIs principaux.'
      },
      {
        id: 4,
        title: 'Aide configuration',
        icon: 'fas fa-cog',
        prompt: 'J\'ai besoin d\'aide pour configurer un nouveau compte email. Peux-tu me guider ?'
      }
    ])
    
    // Computed
    const formattedMessages = computed(() => {
      return messages.value.map(msg => ({
        ...msg,
        formattedContent: formatMessage(msg.content)
      }))
    })
    
    // Méthodes
    const sendMessage = async () => {
      if (!currentMessage.value.trim() || isLoading.value) return
      
      const userMessage = {
        role: 'user',
        content: currentMessage.value.trim(),
        timestamp: new Date(),
        attachments: [...attachedFiles.value]
      }
      
      messages.value.push(userMessage)
      const messageToSend = currentMessage.value.trim()
      currentMessage.value = ''
      attachedFiles.value = []
      suggestions.value = []
      
      await nextTick()
      scrollToBottom()
      
      isLoading.value = true
      
      try {
        // Préparer le contexte
        const context = await buildContext()
        
        // Envoyer le message à l'IA
        const response = await chatStore.sendMessage({
          message: messageToSend,
          model: selectedModel.value,
          context,
          config: modelConfig.value,
          conversationHistory: messages.value.slice(-10) // Garder les 10 derniers messages
        })
        
        const assistantMessage = {
          role: 'assistant',
          content: response.content,
          timestamp: new Date(),
          model: selectedModel.value,
          tokens: response.tokens
        }
        
        messages.value.push(assistantMessage)
        
        // Générer des suggestions
        generateSuggestions(response.content)
        
      } catch (error) {
        console.error('Erreur lors de l\'envoi du message:', error)
        
        const errorMessage = {
          role: 'assistant',
          content: 'Désolé, une erreur s\'est produite lors du traitement de votre message. Veuillez réessayer.',
          timestamp: new Date(),
          isError: true
        }
        
        messages.value.push(errorMessage)
      } finally {
        isLoading.value = false
        await nextTick()
        scrollToBottom()
      }
    }
    
    const buildContext = async () => {
      const context = []
      
      if (contextSettings.value.includeEmails) {
        // Ajouter le contexte des emails récents
        context.push('Emails récents non traités: 15')
        context.push('Emails avec commandes détectées: 8')
      }
      
      if (contextSettings.value.includeCommandes) {
        // Ajouter le contexte des commandes
        context.push('Commandes en attente de validation: 12')
        context.push('Commandes avec anomalies: 3')
      }
      
      if (contextSettings.value.includeClients) {
        // Ajouter le contexte des clients
        context.push('Nouveaux clients cette semaine: 5')
        context.push('Clients avec commandes en cours: 23')
      }
      
      if (contextSettings.value.includeProduits) {
        // Ajouter le contexte des produits
        context.push('Produits en rupture de stock: 7')
        context.push('Nouveaux produits ajoutés: 12')
      }
      
      return context.join('\n')
    }
    
    const formatMessage = (content) => {
      // Convertir le markdown en HTML
      const html = marked(content)
      
      // Ajouter des classes CSS pour le styling
      return html
        .replace(/<code>/g, '<code class="inline-code">')
        .replace(/<pre><code/g, '<pre class="code-block"><code')
        .replace(/<table>/g, '<table class="message-table">')
        .replace(/<blockquote>/g, '<blockquote class="message-quote">')
    }
    
    const generateSuggestions = (responseContent) => {
      // Générer des suggestions basées sur la réponse
      const commonSuggestions = [
        'Peux-tu donner plus de détails ?',
        'Comment puis-je implémenter cela ?',
        'Y a-t-il des alternatives ?',
        'Quels sont les risques ?'
      ]
      
      // Suggestions contextuelles basées sur le contenu
      if (responseContent.includes('email')) {
        suggestions.value = [
          'Analyser d\'autres emails',
          'Configurer les règles d\'extraction',
          'Voir les statistiques d\'emails'
        ]
      } else if (responseContent.includes('commande')) {
        suggestions.value = [
          'Valider les commandes en attente',
          'Détecter les anomalies',
          'Exporter les commandes'
        ]
      } else {
        suggestions.value = commonSuggestions.slice(0, 3)
      }
    }
    
    const useSuggestion = (suggestion) => {
      currentMessage.value = suggestion
      suggestions.value = []
      messageInput.value?.focus()
    }
    
    const useQuickPrompt = (prompt) => {
      currentMessage.value = prompt.prompt
      messageInput.value?.focus()
    }
    
    const handleKeydown = (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        sendMessage()
      }
    }
    
    const adjustTextareaHeight = () => {
      const textarea = messageInput.value
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
      }
    }
    
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    }
    
    const switchModel = () => {
      const modelNames = {
        'llama2': 'Llama 2',
        'codellama': 'Code Llama',
        'mistral': 'Mistral',
        'gpt-4': 'GPT-4',
        'gpt-3.5-turbo': 'GPT-3.5 Turbo'
      }
      
      currentModel.value = modelNames[selectedModel.value] || selectedModel.value
      
      // Simuler la vérification de connexion
      isConnected.value = Math.random() > 0.1 // 90% de chance d'être connecté
    }
    
    const clearConversation = () => {
      if (confirm('Êtes-vous sûr de vouloir effacer cette conversation ?')) {
        messages.value = []
        currentConversationId.value = null
      }
    }
    
    const exportConversation = () => {
      const conversationData = {
        id: currentConversationId.value || Date.now(),
        title: `Conversation ${new Date().toLocaleDateString()}`,
        messages: messages.value,
        model: selectedModel.value,
        timestamp: new Date().toISOString()
      }
      
      const blob = new Blob([JSON.stringify(conversationData, null, 2)], {
        type: 'application/json'
      })
      
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `conversation_${new Date().toISOString().split('T')[0]}.json`
      link.click()
      URL.revokeObjectURL(url)
    }
    
    const loadConversation = (conversationId) => {
      // Simuler le chargement d'une conversation
      currentConversationId.value = conversationId
      
      // Ici, on chargerait les messages depuis l'API
      messages.value = [
        {
          role: 'user',
          content: 'Bonjour, peux-tu m\'aider avec l\'analyse des emails ?',
          timestamp: new Date(Date.now() - 60 * 60 * 1000)
        },
        {
          role: 'assistant',
          content: 'Bien sûr ! Je peux vous aider à analyser vos emails. Que souhaitez-vous faire exactement ?',
          timestamp: new Date(Date.now() - 59 * 60 * 1000)
        }
      ]
    }
    
    const copyMessage = async (content) => {
      try {
        await navigator.clipboard.writeText(content)
        // Afficher une notification de succès
      } catch (error) {
        console.error('Erreur lors de la copie:', error)
      }
    }
    
    const regenerateResponse = async (messageIndex) => {
      if (isLoading.value) return
      
      // Supprimer la réponse actuelle
      messages.value.splice(messageIndex, 1)
      
      // Récupérer le message utilisateur précédent
      const userMessage = messages.value[messageIndex - 1]
      if (userMessage && userMessage.role === 'user') {
        currentMessage.value = userMessage.content
        await sendMessage()
      }
    }
    
    const rateMessage = (message, rating) => {
      message.rating = rating
      
      // Envoyer le feedback à l'API
      chatStore.rateMessage(message.id, rating)
    }
    
    const attachFile = () => {
      const input = document.createElement('input')
      input.type = 'file'
      input.multiple = true
      input.accept = '.pdf,.doc,.docx,.txt,.csv,.xlsx'
      
      input.onchange = (event) => {
        const files = Array.from(event.target.files)
        attachedFiles.value.push(...files)
      }
      
      input.click()
    }
    
    const removeFile = (index) => {
      attachedFiles.value.splice(index, 1)
    }
    
    const toggleVoiceInput = () => {
      if (!('webkitSpeechRecognition' in window)) {
        alert('La reconnaissance vocale n\'est pas supportée par votre navigateur')
        return
      }
      
      if (isListening.value) {
        // Arrêter l'écoute
        isListening.value = false
      } else {
        // Commencer l'écoute
        isListening.value = true
        
        const recognition = new webkitSpeechRecognition()
        recognition.lang = 'fr-FR'
        recognition.continuous = false
        recognition.interimResults = false
        
        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript
          currentMessage.value += transcript
          isListening.value = false
        }
        
        recognition.onerror = () => {
          isListening.value = false
        }
        
        recognition.onend = () => {
          isListening.value = false
        }
        
        recognition.start()
      }
    }
    
    const updateContext = debounce(() => {
      // Mettre à jour le contexte quand les paramètres changent
    }, 300)
    
    const formatDate = (date) => {
      return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    }
    
    const formatTime = (date) => {
      return new Intl.DateTimeFormat('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    }
    
    const closeConfigModal = () => {
      showConfigModal.value = false
    }
    
    const saveConfig = () => {
      // Sauvegarder la configuration
      chatStore.updateConfig(modelConfig.value)
      closeConfigModal()
    }
    
    // Watchers
    watch(messages, () => {
      nextTick(() => {
        scrollToBottom()
      })
    }, { deep: true })
    
    // Lifecycle
    onMounted(() => {
      switchModel()
      messageInput.value?.focus()
    })
    
    return {
      // État
      currentMessage,
      isLoading,
      isListening,
      sidebarCollapsed,
      showConfigModal,
      messagesContainer,
      messageInput,
      attachedFiles,
      suggestions,
      selectedModel,
      currentModel,
      isConnected,
      currentConversationId,
      messages,
      recentConversations,
      contextSettings,
      modelConfig,
      quickPrompts,
      
      // Computed
      formattedMessages,
      
      // Méthodes
      sendMessage,
      formatMessage,
      useSuggestion,
      useQuickPrompt,
      handleKeydown,
      adjustTextareaHeight,
      switchModel,
      clearConversation,
      exportConversation,
      loadConversation,
      copyMessage,
      regenerateResponse,
      rateMessage,
      attachFile,
      removeFile,
      toggleVoiceInput,
      updateContext,
      formatDate,
      formatTime,
      closeConfigModal,
      saveConfig
    }
  }
}
</script>

<style scoped>
.chat-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--gray-50);
}

.chat-header {
  background: white;
  border-bottom: 1px solid var(--border-color);
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.chat-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.chat-title i {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.chat-title h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
}

.model-indicator {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: var(--gray-100);
  color: var(--text-secondary);
}

.model-indicator.online {
  background: var(--success-light);
  color: var(--success-color);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.model-selector select {
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: white;
}

.chat-container {
  flex: 1;
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  overflow: hidden;
}

.chat-sidebar {
  width: 300px;
  background: white;
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  transition: width 0.3s;
}

.chat-sidebar.collapsed {
  width: 60px;
}

.sidebar-header {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.sidebar-toggle {
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: background-color 0.2s;
}

.sidebar-toggle:hover {
  background: var(--gray-100);
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.context-section {
  margin-bottom: 2rem;
}

.context-section h4 {
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.conversation-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.conversation-item {
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid transparent;
}

.conversation-item:hover {
  background: var(--gray-50);
}

.conversation-item.active {
  background: var(--primary-light);
  border-color: var(--primary-color);
}

.conversation-title {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.conversation-date {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.context-toggles {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.context-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--text-primary);
}

.context-toggle input[type="checkbox"] {
  width: auto;
}

.quick-prompts {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.quick-prompt-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--gray-50);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: var(--text-primary);
  text-align: left;
}

.quick-prompt-btn:hover {
  background: var(--primary-light);
  border-color: var(--primary-color);
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.welcome-message {
  text-align: center;
  max-width: 600px;
  margin: 2rem auto;
  color: var(--text-secondary);
}

.welcome-icon {
  font-size: 4rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.welcome-message h2 {
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.welcome-message ul {
  text-align: left;
  margin: 1rem 0;
}

.message {
  display: flex;
  gap: 1rem;
  max-width: 800px;
}

.message.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background: var(--primary-color);
  color: white;
}

.message.assistant .message-avatar {
  background: var(--gray-100);
  color: var(--text-secondary);
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message.user .message-content {
  text-align: right;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.message.user .message-header {
  justify-content: flex-end;
}

.message-role {
  font-weight: 500;
}

.message-text {
  background: var(--gray-50);
  padding: 1rem;
  border-radius: 12px;
  line-height: 1.6;
  color: var(--text-primary);
}

.message.user .message-text {
  background: var(--primary-color);
  color: white;
}

.message-actions {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.message:hover .message-actions {
  opacity: 1;
}

.action-btn {
  background: none;
  border: none;
  padding: 0.25rem;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
}

.action-btn:hover {
  background: var(--gray-100);
  color: var(--text-primary);
}

.action-btn.active {
  color: var(--primary-color);
}

.typing {
  opacity: 0.7;
}

.typing-indicator {
  display: flex;
  gap: 0.25rem;
  padding: 1rem;
  background: var(--gray-50);
  border-radius: 12px;
  width: fit-content;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--text-secondary);
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.input-container {
  border-top: 1px solid var(--border-color);
  padding: 1.5rem 2rem;
  background: white;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  max-width: 800px;
  margin: 0 auto;
}

.input-actions {
  display: flex;
  gap: 0.25rem;
}

.input-wrapper textarea {
  flex: 1;
  min-height: 44px;
  max-height: 120px;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 22px;
  resize: none;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
}

.input-wrapper textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.send-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: scale(1.05);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.attached-files {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.attached-file {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--gray-100);
  border-radius: 6px;
  font-size: 0.75rem;
  color: var(--text-primary);
}

.attached-file button {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 2px;
}

.attached-file button:hover {
  color: var(--danger-color);
}

.suggestions {
  margin-top: 1rem;
}

.suggestions-header {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
}

.suggestions-list {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.suggestion-btn {
  padding: 0.5rem 0.75rem;
  background: var(--gray-100);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  font-size: 0.75rem;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.suggestion-btn:hover {
  background: var(--primary-light);
  border-color: var(--primary-color);
}

/* Styles pour le contenu des messages */
.message-text :deep(.inline-code) {
  background: rgba(0, 0, 0, 0.1);
  padding: 0.125rem 0.25rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.875em;
}

.message.user .message-text :deep(.inline-code) {
  background: rgba(255, 255, 255, 0.2);
}

.message-text :deep(.code-block) {
  background: var(--gray-100);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
  overflow-x: auto;
}

.message.user .message-text :deep(.code-block) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.message-text :deep(.message-table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5rem 0;
}

.message-text :deep(.message-table th),
.message-text :deep(.message-table td) {
  border: 1px solid var(--border-color);
  padding: 0.5rem;
  text-align: left;
}

.message-text :deep(.message-table th) {
  background: var(--gray-50);
  font-weight: 600;
}

.message-text :deep(.message-quote) {
  border-left: 4px solid var(--primary-color);
  padding-left: 1rem;
  margin: 0.5rem 0;
  font-style: italic;
  color: var(--text-secondary);
}

/* Variables CSS */
/* :root ne matche rien en style scoped : variables portées par la racine du composant */
.chat-page {
  --primary-color: #667eea; /* violet de l'app */
  --primary-light: #e9ecfc;
  --success-color: #10b981;
  --success-light: #d1fae5;
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
  --text-primary: #1f2937;
  --text-secondary: #6b7280;
  --gray-50: #f9fafb;
  --gray-100: #f3f4f6;
  --border-color: #e5e7eb;
}

/* Responsive */
@media (max-width: 768px) {
  .chat-header {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .messages-container {
    padding: 1rem;
  }
  
  .input-container {
    padding: 1rem;
  }
  
  .message {
    max-width: 100%;
  }
}

/* Styles pour les modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
  max-width: 600px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background: var(--gray-100);
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
}

.config-section {
  margin-bottom: 2rem;
}

.config-section h4 {
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 600;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.form-group label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 0.875rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: var(--gray-100);
  color: var(--text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--gray-200);
}

.btn-outline {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.btn-outline:hover:not(:disabled) {
  background: var(--gray-50);
  color: var(--text-primary);
}

/* ---------- Responsive ---------- */

.chat-sidebar.collapsed .sidebar-header h3 {
  display: none;
}

.chat-main {
  min-width: 0;
  min-height: 0;
}

.messages-container {
  min-height: 0;
}

.message-text {
  overflow-wrap: anywhere;
}

.message-text :deep(.message-table) {
  display: block;
  overflow-x: auto;
}

.conversation-title,
.attached-file span {
  overflow-wrap: anywhere;
  min-width: 0;
}

@media (max-width: 991.98px) {
  /* Remplit la hauteur visible sous la barre supérieure (60px) moins le padding de .main-content */
  .chat-page {
    height: calc(100vh - 60px - 2.75rem);
    height: calc(100dvh - 60px - env(safe-area-inset-top) - 2.75rem);
    min-height: 420px;
  }

  .chat-header {
    padding: 0.75rem 1rem;
  }

  .header-content {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .chat-title {
    min-width: 0;
    flex-wrap: wrap;
    gap: 0.5rem 0.75rem;
  }

  .chat-title h1 {
    font-size: 1.25rem;
    min-width: 0;
  }

  .header-actions {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* Le panneau de contexte passe au-dessus de la conversation, repliable */
  .chat-container {
    flex-direction: column;
    min-height: 0;
  }

  .chat-sidebar,
  .chat-sidebar.collapsed {
    width: 100%;
    flex-shrink: 0;
    border-right: none;
    border-bottom: 1px solid var(--border-color);
  }

  .chat-sidebar.collapsed .sidebar-header h3 {
    display: block;
  }

  .sidebar-header {
    padding: 0.5rem 1rem;
    border-bottom: none;
  }

  .chat-sidebar:not(.collapsed) .sidebar-header {
    border-bottom: 1px solid var(--border-color);
  }

  .sidebar-toggle i {
    transform: rotate(90deg);
  }

  .sidebar-content {
    max-height: 40vh;
    max-height: 40dvh;
    overscroll-behavior: contain;
  }

  .chat-main {
    flex: 1;
  }
}

@media (max-width: 767.98px) {
  .header-content {
    flex-direction: row;
  }

  .header-actions {
    width: 100%;
  }

  .model-selector {
    flex: 1 1 100px;
    min-width: 0;
  }

  .model-selector select {
    width: 100%;
    min-width: 0;
  }

  .welcome-message {
    margin: 0.5rem auto;
  }

  .welcome-icon {
    font-size: 3rem;
  }

  .welcome-message h2 {
    font-size: 1.25rem;
  }

  .message {
    gap: 0.625rem;
  }

  .message-avatar {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }

  .input-container {
    padding: 0.75rem;
  }

  .input-wrapper {
    gap: 0.375rem;
  }

  .input-actions {
    gap: 0;
  }

  /* Modal plein écran */
  .modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .modal-content {
    max-width: none;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .modal-header {
    padding-top: max(1.5rem, env(safe-area-inset-top));
  }

  .modal-footer {
    padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
  }
}

/* Pas de survol sur écran tactile : actions toujours visibles, cibles >= 44px */
@media (hover: none), (pointer: coarse) {
  .message-actions {
    opacity: 1;
  }
}

@media (pointer: coarse) {
  .action-btn,
  .sidebar-toggle,
  .modal-close,
  .attached-file button {
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .action-btn {
    font-size: 1rem;
  }
}
</style>